import React, { useEffect, useMemo } from 'react'
import Media from 'react-media'
import { Route, Switch } from 'react-router-dom'
import config from './config'
import EntrancePages from './containers/EntrancePages'
import PrivateRoute from './containers/PrivateRoute'
import { SingleSignOnHandler } from './containers/SingleSignOnHandler'
import MobileAuditorium from './mobileOnlyPages/MobileAuditorium/index.js'
import MobileExpoBooth from './mobileOnlyPages/MobileExpo/MobileExpoBooth.js'
import MobileExpoDirectory from './mobileOnlyPages/MobileExpo/MobileExpoDirectory.js'
import MobileLobby from './mobileOnlyPages/MobileLobby/index.js'
import MobileMenu from './mobileOnlyPages/MobileMenu/index.js'
import MobileFaq from './mobileOnlyPages/MobileFaq/index.js'
import MobileNetworking from './mobileOnlyPages/MobileNetworking/index.js'
import MobileNotifications from './mobileOnlyPages/MobileNotifications/index.js'
import { MobileOnDemand } from './mobileOnlyPages/MobileOnDemand/'
import { MobileTheatre, MobileVirtualProductLanding } from './mobileOnlyPages/MobileVirtualProductTheatre'
import ActiveUserCountPage from './pages/ActiveUserCountPage'
import { AdminLiveQuestionPage } from './pages/AdminLiveQuestionPage'
import CountdownPage from './pages/CountdownPage'
import LandingPage from './pages/LandingPage/LandingPage'
import SetPasswordFormPage from './pages/registration/SetPasswordFormPage'
import SessionReportPage from './pages/SessionReportPage'
import UserSessionPage from './pages/UserSessionPage'
import routes from './routes'
import { SIZES } from './styles/theme'
import AuditoriumPage from './webOnlyPages/AuditoriumPage'
import BoothPage from './webOnlyPages/BoothPage'
import { DesktopOnDemand } from './webOnlyPages/DesktopOnDemand'
import ExhibitionPage from './webOnlyPages/ExhibitionPage'
import { LobbyPage } from './webOnlyPages/LobbyPage'
import NetworkingLoungePage from './webOnlyPages/NetworkingLoungePage'
import { TheatrePage, VirtualProductLandingPage } from './webOnlyPages/VirtualProductTheatrePage'

declare global {
  interface Window {
    chaport: any
  }
}

function App() {
  useEffect(() => {
    document.title = config.eventName
  }, [])

  useEffect(() => {
    window.chaport.q('hideLauncher')
  }, [])

  return useMemo(() => {
    return (
      <div id="innerBody">
        <Switch>
          <Route exact path={routes.landing} component={LandingPage} />
          <Route exact path={routes.root} component={CountdownPage} />

          <Route exact path={routes.resetPassword} component={SetPasswordFormPage} />
          <Route exact path="/session-reports" component={SessionReportPage} />
          <PrivateRoute blockedRoles={['participant', 'exhibitor', 'speaker']} exact path="/realtime" component={ActiveUserCountPage} />
          <PrivateRoute blockedRoles={['participant', 'exhibitor', 'speaker']} exact path="/sessions" component={UserSessionPage} />
          <PrivateRoute blockedRoles={['participant', 'exhibitor', 'speaker']} exact path="/live-questions/:hallNumber" component={AdminLiveQuestionPage} />
          <Route exact path={routes.login} component={EntrancePages} />
          <Route exact path={routes.sso} component={SingleSignOnHandler} />
        </Switch>
        <Media query={`(max-width: ${SIZES.mobileBreakpoint})`}>
          {(matches) =>
            matches ? (
              // MOBILE ROUTES
              <Switch>
                <PrivateRoute path={routes.lobby} component={MobileLobby} mobileNav />

                {/* Auditorium */}
                <PrivateRoute
                  mobileNav
                  path={routes.auditorium1}
                  component={(props) => <MobileAuditorium {...props} hallNumber={1} liveUrl="https://vimeo.com/event/1349755/embed/aede921eb0" />}
                />

                <PrivateRoute path={routes.networking} component={MobileNetworking} mobileNav />
                <PrivateRoute path="/exhibition-hall/:hallId" component={MobileExpoDirectory} mobileNav />
                <PrivateRoute path="/booths/:boothId" component={MobileExpoBooth} mobileNav />
                <PrivateRoute path="/menu" component={MobileMenu} mobileNav />
                <PrivateRoute path="/faq" component={MobileFaq} mobileNav />
                <PrivateRoute path={routes.notifications} component={MobileNotifications} mobileNav />
                <PrivateRoute path={routes.onDemand} component={MobileOnDemand} mobileNav />
                <PrivateRoute path={routes.virtualProductLanding} component={MobileVirtualProductLanding} mobileNav />
                <PrivateRoute
                  path={routes.smithNephew}
                  component={(props) => <MobileTheatre {...props} hallNumber={1} liveUrl="https://player.vimeo.com/video/628935691" />}
                  mobileNav
                />
                <PrivateRoute
                  path={routes.malexMedicalAsia1}
                  component={(props) => <MobileTheatre {...props} hallNumber={2} liveUrl="https://player.vimeo.com/video/630160135" />}
                  mobileNav
                />
                <PrivateRoute
                  path={routes.malexMedicalAsia2}
                  component={(props) => <MobileTheatre {...props} hallNumber={3} liveUrl="https://player.vimeo.com/video/631736772" />}
                  mobileNav
                />
              </Switch>
            ) : (
              // DESKTOP ROUTES
              <Switch>
                <PrivateRoute path={routes.lobby} component={LobbyPage} fixedNav />
                <PrivateRoute path={routes.networking} component={NetworkingLoungePage} fixedNav />

                {/* Auditorium */}
                <PrivateRoute
                  fixedNav
                  path={routes.auditorium1}
                  component={(props) => <AuditoriumPage {...props} hallNumber={1} liveUrl="https://vimeo.com/event/1349755/embed/aede921eb0" />}
                />

                <PrivateRoute path="/exhibition-hall/:hallId" component={ExhibitionPage} fixedNav />
                <PrivateRoute path="/booths/:boothId" component={BoothPage} fixedNav />
                <PrivateRoute path={routes.onDemand} component={DesktopOnDemand} fixedNav />
                <PrivateRoute path={routes.virtualProductLanding} component={VirtualProductLandingPage} fixedNav />
                <PrivateRoute
                  path={routes.smithNephew}
                  component={(props) => <TheatrePage {...props} hallNumber={1} liveUrl="https://player.vimeo.com/video/628935691" />}
                  fixedNav
                />
                <PrivateRoute
                  path={routes.malexMedicalAsia1}
                  component={(props) => <TheatrePage {...props} hallNumber={2} liveUrl="https://player.vimeo.com/video/630160135" />}
                  fixedNav
                />
                <PrivateRoute
                  path={routes.malexMedicalAsia2}
                  component={(props) => <TheatrePage {...props} hallNumber={3} liveUrl="https://player.vimeo.com/video/631736772" />}
                  fixedNav
                />
              </Switch>
            )
          }
        </Media>
      </div>
    )
  }, [])
}

export default App
